import { Outlet, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import type { MeDTO, WorkspaceDTO } from '@routr/shared'
import { LoadFailed, Loading, NotFound } from '@/components/shell/Loading'
import { UiModeProvider } from '@/components/workspace/UiModeProvider'
import { WorkspaceRail } from '@/components/workspace/WorkspaceRail'
import { rememberWorkspace, useApi, useMe } from '@/lib/api'

export type WorkspaceShell = { me: MeDTO; workspace: WorkspaceDTO }

/**
 * The workspace chrome: resolves the session and the membership once, then
 * renders the rail and the page. A workspace the person is not part of
 * reads as 404, as the API answers.
 */
export function WorkspaceLayout({ slug }: { slug: string }) {
  const me = useMe()
  const ws = useApi<WorkspaceDTO>(me.data ? `/api/w/${slug}` : null)
  const navigate = useNavigate()
  useEffect(() => {
    if (me.data === null) void navigate({ to: '/login', search: { next: `/w/${slug}/projects` } as never })
  }, [me.data, navigate, slug])
  useEffect(() => {
    if (ws.data) rememberWorkspace(ws.data.slug)
  }, [ws.data])

  if (me.isPending || (me.data && ws.isPending)) return <Loading />
  if (me.isError) return <LoadFailed error={me.error} retry={() => void me.refetch()} />
  if (!me.data) return <Loading />
  if (ws.isError) {
    const status = (ws.error as { status?: number }).status
    return status === 404 ? <NotFound /> : <LoadFailed error={ws.error} retry={() => void ws.refetch()} />
  }
  if (!ws.data) return <Loading />

  return (
    <UiModeProvider initial={me.data.uiMode}>
      <div className="flex min-h-dvh">
        <WorkspaceRail current={ws.data} role={ws.data.role} workspaces={me.data.workspaces} userEmail={me.data.email} isPlatformAdmin={me.data.isPlatformAdmin} />
        <main className="flex min-h-0 min-w-0 flex-1 flex-col pb-24 sm:pb-0">
          <Outlet />
        </main>
      </div>
    </UiModeProvider>
  )
}
